import type { TFunction } from "i18next";
import type { SubjectConfiguration, ValidationPresentation } from "../../types";
import { hasInvalidVacancySource } from "../group-vacancy-source";

export const validateLeagueConfiguration = (
  configurations: SubjectConfiguration[],
  canCreateWithoutNewSections: boolean,
  t: TFunction,
): Map<string, ValidationPresentation[]> => {
  const errors = new Map<string, ValidationPresentation[]>();

  configurations.forEach(({ subject, choice }) => {
    const messages: ValidationPresentation[] = [];
    if (choice.kind === "use-existing" && !choice.existingLeagueId) {
      messages.push({
        reason: "EXISTING_LEAGUE_REQUIRED",
        message: t("package-wizard.leagues.validation.existing-required"),
        scope: "subject",
        discardable: false,
      });
    }

    if (choice.kind === "create-new") {
      if (!subject.components.length || choice.sections.length !== subject.components.length) {
        messages.push({
          reason: "SECTION_CONFIGURATION_REQUIRED",
          message: t("package-wizard.leagues.validation.sections-required"),
          scope: "subject",
          discardable: false,
        });
      }

      if (choice.sections.some(section => section.kind === "use-existing" && !section.sectionId)) {
        messages.push({
          reason: "EXISTING_SECTION_REQUIRED",
          message: t("create-group-link.step-2.component.section-existing.error-message"),
          scope: "subject",
          discardable: false,
        });
      }

      if (
        !canCreateWithoutNewSections &&
        !choice.sections.some(section => section.kind === "create-new")
      ) {
        messages.push({
          reason: "NEW_SECTION_REQUIRED",
          message: t("create-group-link.step-2.component.new-section.error-message"),
          scope: "subject",
          discardable: false,
        });
      }
    }

    if (messages.length) errors.set(subject.courseId, messages);
  });

  return errors;
};

export const validateInitialGroupConfiguration = (
  configurations: SubjectConfiguration[],
  formatErrors: Record<string, readonly unknown[]>,
  t: TFunction,
): Map<string, ValidationPresentation[]> => {
  const errors = new Map<string, ValidationPresentation[]>();

  configurations.forEach(({ subject, choice }) => {
    if (choice.kind !== "create-new") return;
    const messages: ValidationPresentation[] = [];

    if (!choice.group.code || formatErrors[subject.courseId]?.length) {
      messages.push({
        reason: "GROUP_CODE_REQUIRED_OR_INVALID",
        message: t("package-wizard.groups.validation.code"),
        scope: "group",
        discardable: false,
        field: "group-code",
      });
    }

    const vacancies = Number(choice.group.vacancies);
    const vacancyReason = hasInvalidVacancySource(subject, choice.group)
      ? "GROUP_VACANCIES_SOURCE_INVALID"
      : !choice.group.vacancies || !Number.isInteger(vacancies) || vacancies <= 0
        ? "GROUP_VACANCIES_REQUIRED"
        : null;
    if (vacancyReason) {
      messages.push({
        reason: vacancyReason,
        message:
          vacancyReason === "GROUP_VACANCIES_SOURCE_INVALID"
            ? t("package-wizard.validation.GROUP_VACANCIES_SOURCE_INVALID")
            : t("package-wizard.groups.validation.vacancies"),
        scope: "group",
        discardable: false,
        field: "group-vacancies",
      });
    }

    if (messages.length) errors.set(subject.courseId, messages);
  });

  return errors;
};
